import React, { useState, useEffect, useMemo } from 'react';
import { mutate as globalMutate } from 'swr';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { Switch } from '../../../components/ui/Switch';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';
import { accountSharesSwrKey } from './accountShares';

interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
}

interface ExistingShare {
  shareId: number;
  sharedWithUserId: number;
  sharedWithUserName: string;
  permission: 'read' | 'write';
  showSentFolder?: boolean | null;
  showMySentFolder?: boolean | null;
  showInAccountsSidebar?: boolean | null;
}

interface ShareAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: number | null;
  accountName: string;
  defaultShowInAccountsSidebar?: boolean;
  /** Called after new shares are saved (modal will close). */
  onShared?: () => void;
  /** Called after a share is removed (modal stays open). */
  onSharesChanged?: () => void;
}

const UserIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4 21a8 8 0 0116 0H4z" />
  </svg>
);

const AccountIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const SpinnerIcon = ({ className }: { className?: string }) => (
  <svg className={cn('animate-spin', className)} fill="none" viewBox="0 0 24 24" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const ShareAccountModal: React.FC<ShareAccountModalProps> = ({
  isOpen,
  onClose,
  accountId,
  accountName,
  defaultShowInAccountsSidebar = true,
  onShared,
  onSharesChanged,
}) => {
  const { colors } = useTheme();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [existingShares, setExistingShares] = useState<ExistingShare[]>([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [revokingShareId, setRevokingShareId] = useState<number | null>(null);
  const [updatingShareId, setUpdatingShareId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rowBusy = saving || revokingShareId != null || updatingShareId != null;

  useEffect(() => {
    if (isOpen) {
      setSelectedUserIds([]);
      setError(null);
      setUsersLoading(true);
      api
        .get<UserOption[]>('/users')
        .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
        .catch(() => setUsers([]))
        .finally(() => setUsersLoading(false));

      if (accountId != null) {
        setSharesLoading(true);
        api
          .get<ExistingShare[]>(`/emails/accounts/${accountId}/shares`)
          .then((r) => setExistingShares(Array.isArray(r.data) ? r.data : []))
          .catch(() => setExistingShares([]))
          .finally(() => setSharesLoading(false));
      } else {
        setExistingShares([]);
      }
    }
  }, [isOpen, accountId]);

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const handleRemoveShare = async (shareId: number) => {
    setRevokingShareId(shareId);
    setError(null);
    try {
      await api.delete(`/emails/accounts/shares/${shareId}`);
      setExistingShares((prev) => prev.filter((s) => s.shareId !== shareId));
      await globalMutate('/emails/accounts/shared-with-me');
      await globalMutate('/email-accounts/manage');
      if (accountId != null) {
        await globalMutate(accountSharesSwrKey(accountId));
      }
      onSharesChanged?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to remove share');
    } finally {
      setRevokingShareId(null);
    }
  };

  const handleShareFolderToggle = async (
    shareId: number,
    field: 'showSentFolder' | 'showMySentFolder' | 'showInAccountsSidebar',
    value: boolean,
  ) => {
    setUpdatingShareId(shareId);
    setError(null);
    try {
      await api.patch(`/emails/accounts/shares/${shareId}`, { [field]: value });
      setExistingShares((prev) =>
        prev.map((s) => (s.shareId === shareId ? { ...s, [field]: value } : s)),
      );
      await globalMutate('/emails/accounts/shared-with-me');
      await globalMutate('/email-accounts/manage');
      if (accountId != null) {
        await globalMutate(accountSharesSwrKey(accountId));
      }
      onSharesChanged?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to update share settings');
    } finally {
      setUpdatingShareId(null);
    }
  };

  const shareFolderSwitch = (
    share: ExistingShare,
    field: 'showSentFolder' | 'showMySentFolder' | 'showInAccountsSidebar',
    label: string,
  ) => {
    const inherited =
      field === 'showSentFolder'
        ? share.showSentFolder
        : field === 'showMySentFolder'
          ? share.showMySentFolder
          : share.showInAccountsSidebar;
    const accountDefault =
      field === 'showInAccountsSidebar' ? defaultShowInAccountsSidebar : true;
    const effective = inherited ?? accountDefault;
    const disabled = rowBusy || updatingShareId === share.shareId;

    return (
      <div className="flex items-center gap-2 min-w-[5.5rem]">
        <Switch
          checked={effective}
          disabled={disabled}
          size="sm"
          onChange={(e) => void handleShareFolderToggle(share.shareId, field, e.target.checked)}
          aria-label={`${label} for ${share.sharedWithUserName}`}
        />
        <span className={cn('text-[11px] font-medium whitespace-nowrap', colors.textMuted)}>{label}</span>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (accountId == null || selectedUserIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const sharedWithUserId of selectedUserIds) {
        await api.post(`/emails/accounts/${accountId}/share`, {
          sharedWithUserId,
          permission: 'read',
        });
      }
      await globalMutate('/emails/accounts/shared-with-me');
      await globalMutate('/email-accounts/manage');
      if (accountId != null) {
        await globalMutate(accountSharesSwrKey(accountId));
      }
      onShared?.();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to share account');
    } finally {
      setSaving(false);
    }
  };

  const userLabel = (u: UserOption) =>
    u.userDisplayName?.trim() ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ') ||
    u.userEmail ||
    `User #${u.id}`;

  const sharedUserIds = useMemo(
    () => new Set(existingShares.map((s) => s.sharedWithUserId)),
    [existingShares],
  );

  const usersToShare = useMemo(
    () => users.filter((u) => !sharedUserIds.has(u.id)),
    [users, sharedUserIds],
  );

  const tableHeaderClass = cn(
    'grid gap-3 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider',
    colors.textMuted,
    'bg-black/5 dark:bg-white/5',
  );

  const avatarClass = (variant: 'shared' | 'selected' | 'default') =>
    cn(
      'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5',
      variant === 'shared' && 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
      variant === 'selected' && 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400',
      variant === 'default' && 'bg-gray-100 dark:bg-gray-800 text-gray-400',
    );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Share email account</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        {accountName && (
          <div
            className={cn(
              'flex items-center gap-2.5 px-4 py-3 rounded-xl mb-4',
              'bg-black/5 dark:bg-white/5',
              colors.text,
            )}
          >
            <span className={cn('opacity-70', colors.textMuted)}>{AccountIcon}</span>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wider opacity-60">Email account</p>
              <p className="text-sm font-medium truncate">{accountName}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {(sharesLoading || existingShares.length > 0) && (
            <div>
              <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Already shared with</h3>
              <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
                <div
                  className={cn(
                    tableHeaderClass,
                    'grid-cols-[minmax(0,1fr)_auto_auto_auto]',
                  )}
                >
                  <span>User</span>
                  <span className="hidden sm:inline">Folders</span>
                  <span className="text-center w-16">Access</span>
                  <span className="w-10" />
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sharesLoading ? (
                    <div className={cn('px-4 py-6 text-center text-sm', colors.textMuted)}>
                      Loading shared users...
                    </div>
                  ) : (
                    existingShares.map((s) => (
                      <div
                        key={s.shareId}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-center px-4 py-3',
                          colors.text,
                        )}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={avatarClass('shared')}>{UserIcon}</span>
                          <span className="text-sm font-medium truncate">{s.sharedWithUserName}</span>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 justify-end sm:justify-start">
                          {shareFolderSwitch(s, 'showInAccountsSidebar', 'Accounts')}
                          {shareFolderSwitch(s, 'showSentFolder', 'Sent')}
                          {shareFolderSwitch(s, 'showMySentFolder', 'My sent')}
                        </div>

                        <span
                          className={cn(
                            'inline-flex justify-center w-16 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                          )}
                        >
                          {s.permission}
                        </span>

                        <div className="flex justify-end w-10">
                          <button
                            type="button"
                            disabled={rowBusy}
                            aria-busy={revokingShareId === s.shareId}
                            onClick={() => void handleRemoveShare(s.shareId)}
                            title={`Remove access for ${s.sharedWithUserName}`}
                            aria-label={`Remove share for ${s.sharedWithUserName}`}
                            className={cn(
                              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                              'text-gray-500 hover:bg-red-50 hover:text-red-600',
                              'dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400',
                              'disabled:opacity-50 disabled:pointer-events-none',
                            )}
                          >
                            {revokingShareId === s.shareId ? (
                              <SpinnerIcon className="h-4 w-4" />
                            ) : (
                              <TrashIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Share with more users</h3>
            <p className={cn('text-xs mb-3', colors.textMuted)}>
              Select users to share with. After sharing, adjust Accounts / Sent / My sent visibility per user.
              Users with Accounts off can still access mail via a shared Smart Folder inbox.
            </p>

            {usersLoading ? (
              <div className={cn('flex items-center justify-center py-12', colors.textMuted)}>
                <span className="animate-pulse">Loading users...</span>
              </div>
            ) : usersToShare.length === 0 ? (
              <div
                className={cn(
                  'rounded-xl border border-dashed py-10 text-center text-sm',
                  colors.border,
                  colors.textMuted,
                )}
              >
                {users.length === 0
                  ? 'No users available to share with.'
                  : 'All users already have access to this account.'}
              </div>
            ) : (
              <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
                <div className={cn(tableHeaderClass, 'grid-cols-[minmax(0,1fr)_auto]')}>
                  <span>User</span>
                  <span className="w-10" />
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                  {usersToShare.map((u) => {
                    const selected = selectedUserIds.includes(u.id);
                    const label = userLabel(u);

                    return (
                      <div
                        key={u.id}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center px-4 py-3 transition-colors',
                          selected && 'bg-indigo-50/50 dark:bg-indigo-900/20',
                        )}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={avatarClass(selected ? 'selected' : 'default')}>{UserIcon}</span>
                          <div className="min-w-0">
                            <span className="text-sm font-medium truncate block">{label}</span>
                            {selected && (
                              <span className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400">
                                Will be shared
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-end w-10">
                          <button
                            type="button"
                            onClick={() => toggleUser(u.id)}
                            disabled={saving}
                            title={selected ? `Remove ${label} from selection` : `Add ${label}`}
                            aria-label={selected ? `Remove ${label} from selection` : `Add ${label}`}
                            aria-pressed={selected}
                            className={cn(
                              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                              selected
                                ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300 dark:hover:bg-indigo-900/70'
                                : cn(
                                    'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800',
                                    colors.interactiveHover,
                                  ),
                            )}
                          >
                            {selected ? <CheckIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div
            className={cn(
              'mt-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm',
              'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
            )}
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={() => void handleSubmit()}
          disabled={saving || selectedUserIds.length === 0 || accountId == null}
        >
          {saving ? 'Sharing...' : `Share with ${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShareAccountModal;
